import { create } from 'zustand';
import { makeGet, makePatch, makePost } from '../../utils/call-api-endpoint';
import type { PaginatedList } from '../common/padinated-list';
import {
  toQueryString,
  type PaginationQueryParamsWithStatus
} from '../common/pagination-query-params';
import { useUIStore } from '../ui/ui.store';
import type { Almacen } from './almacen';

export interface AlmacenesState {
  list: PaginatedList<Almacen>;
  current: Almacen;
  actives: Almacen[];

  nuevo: () => void;
  cargarPaginados: (params: PaginationQueryParamsWithStatus) => Promise<void>;
  cargarActivos: () => Promise<void>;
  cargarPorId: (id: string) => Promise<void>;
  registrar: (request: RegistrarAlmacen) => Promise<boolean>;
  actualizar: (id: string, request: ActualizarAlmacen) => Promise<boolean>;
  activar: (id: string) => Promise<boolean>;
  desactivar: (id: string) => Promise<boolean>;
}

export interface RegistrarAlmacen {
  nombre: string;
  permiteMovsSinExistencia: boolean;
}

export interface ActualizarAlmacen {
  nombre: string;
  permiteMovsSinExistencia: boolean;
}

const { showSuccessToast } = useUIStore.getState();

export const useAlmacenesStore = create<AlmacenesState>()((set, get) => ({
  list: {
    items: [],
    pageIndex: 0,
    pageSize: 10,
    totalRows: 0
  },
  current: {} as Almacen,
  actives: [],

  nuevo: () =>
    set({
      current: {} as Almacen
    }),
  cargarPaginados: async (params: PaginationQueryParamsWithStatus) => {
    const data = await makeGet<PaginatedList<Almacen>>(
      `/almacenes/paginados?${toQueryString(params)}`
    );

    if (data.isFailure) return;

    set({
      list: data.result
    });
  },
  cargarActivos: async () => {
    const data = await makeGet<Array<Almacen>>('/almacenes/activos');

    if (data.isFailure) return;

    set({
      actives: data.result
    });
  },
  cargarPorId: async (id: string) => {
    const data = await makeGet<Almacen>(`/almacenes/${id}`);

    if (data.isFailure) return;

    set({
      current: data.result
    });
  },
  registrar: async (request: RegistrarAlmacen) => {
    const data = await makePost<RegistrarAlmacen, Almacen>('/almacenes/', request);

    if (data.isFailure) return false;

    showSuccessToast('Registro agregado');

    set({
      current: {} as Almacen
    });

    return true;
  },
  actualizar: async (id: string, request: ActualizarAlmacen) => {
    const data = await makePatch<ActualizarAlmacen, Almacen>(`/almacenes/${id}`, request);

    if (data.isFailure) return false;

    showSuccessToast('Registro actualizado');

    set({
      current: {
        ...get().current,
        ...request
      }
    });

    return true;
  },
  activar: async (id: string) => {
    const data = await makePatch(`/almacenes/${id}/activar`);

    if (data.isFailure) return false;

    showSuccessToast('Registro activado');

    set({
      current: {
        ...get().current,
        activo: true
      }
    });

    return true;
  },
  desactivar: async (id: string) => {
    const data = await makePatch(`/almacenes/${id}/desactivar`);

    if (data.isFailure) return false;

    showSuccessToast('Registro desactivado');

    set({
      current: {
        ...get().current,
        activo: false
      }
    });

    return true;
  }
}));
